import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FiFileText,
  FiBookOpen,
  FiDollarSign,
  FiHome,
  FiVideo,
  FiInfo,
  FiShield,
  FiAlertTriangle,
  FiExternalLink,
  FiCheckCircle,
  FiXCircle,
  FiLock,
  FiUsers,
  FiAward,
  FiCompass,
  FiMail,
} from "react-icons/fi";
import Header from "../../components/layouts/Header";
import Footer from "../../components/layouts/Footer";
import ContactStrip from "./ContactStrip";
import "./FooterPages.css";

const TOC_SECTIONS = [
  { id: "intro", label: "1. Introduction", icon: <FiBookOpen /> },
  { id: "definitions", label: "2. Definitions", icon: <FiFileText /> },
  { id: "listings", label: "3. Listings & Ads", icon: <FiHome /> },
  { id: "payment", label: "4. Payment & Refunds", icon: <FiDollarSign /> },
  { id: "rentals", label: "5. Rental Services", icon: <FiHome /> },
  { id: "video", label: "6. Video Guidelines", icon: <FiVideo /> },
  { id: "info-use", label: "7. Use of Information", icon: <FiInfo /> },
  { id: "ip-rights", label: "8. IP Rights", icon: <FiAward /> },
  { id: "restrictions", label: "9. Restrictions", icon: <FiAlertTriangle /> },
  { id: "disclaimers", label: "10. Disclaimers & Warranties", icon: <FiShield /> },
  { id: "liability", label: "11. Limitation of Liability", icon: <FiXCircle /> },
  { id: "termination", label: "12. Termination & Indemnity", icon: <FiCheckCircle /> },
  { id: "dealers", label: "13. User & Dealer Terms", icon: <FiUsers /> },
  { id: "legal", label: "14. Governing Law & Arbitration", icon: <FiCompass /> },
  { id: "grievances", label: "15. Grievances & Contact", icon: <FiMail /> },
];

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState("intro");

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="mgh-info-page-wrapper">
      <Helmet>
        <title>Terms & Conditions | My Golden Homes</title>
        <meta
          name="description"
          content="Review the terms and conditions governing your access and use of My Golden Homes real estate platform and services."
        />
      </Helmet>

      {/* Existing Header */}
      <Header />

      {/* Hero Banner */}
      <section className="mgh-info-hero">
        <div className="mgh-info-hero-inner">
          <div className="mgh-info-hero-left">
            <div className="mgh-info-breadcrumb">
              <Link to="/">Home</Link>
              <span>&gt;</span>
              <span>Terms & Conditions</span>
            </div>
            <h1 className="mgh-info-hero-title">
              Terms &amp; <span>Conditions</span>
            </h1>
            <p className="mgh-info-hero-subtitle">
              Please read these terms carefully. By accessing or using My Golden Homes,
              you agree to be bound by the terms and conditions set forth below.
            </p>
            <div className="mgh-hero-pills-row">
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-text">
                  <strong>Effective</strong>
                  <span>25th March 2021</span>
                </div>
              </div>
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-text">
                  <strong>Intermediary</strong>
                  <span>IT Act 2000 Compliant</span>
                </div>
              </div>
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-text">
                  <strong>RERA</strong>
                  <span>Applicable Guidelines</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mgh-info-hero-art">
            <img
              src="/assets/images/villa_header_banner.jpg"
              alt="My Golden Homes Terms"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/assets/images/why_choose_villa.jpg";
              }}
            />
          </div>
        </div>
      </section>

      {/* Main Container with Sidebar + Content */}
      <div className="mgh-info-container">
        <div className="mgh-info-grid-sidebar">
          {/* Sticky Left Navigation Sidebar */}
          <aside className="mgh-sidebar-nav-card">
            {TOC_SECTIONS.map((sec) => (
              <button
                type="button"
                key={sec.id}
                className={`mgh-sidebar-nav-item ${activeSection === sec.id ? "active" : ""}`}
                onClick={() => scrollToSection(sec.id)}
              >
                <span className="mgh-sidebar-nav-icon">{sec.icon}</span>
                <span>{sec.label}</span>
              </button>
            ))}
          </aside>

          {/* Content Body */}
          <div className="mgh-info-main-content">
          {/* Important Notice */}
          <div className="mgh-card" style={{ borderLeft: "4px solid #1e56a0", background: "linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)" }}>
            <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
              <div style={{ fontSize: "24px", color: "#1e56a0", marginTop: "2px" }}>
                <FiLock />
              </div>
              <div>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "16px", color: "#0f2b48", fontWeight: 700 }}>
                  Important Legal Agreement
                </h4>
                <p style={{ margin: 0, fontSize: "14px", color: "#4a5568", lineHeight: 1.6 }}>
                  By accessing or using My Golden Homes (the &ldquo;Platform&rdquo;), you agree to be bound by the terms and conditions set forth below. If you do not agree with any of these terms, you should not access or use My Golden Homes. For legal inquiries, please contact{" "}
                  <a href="mailto:legal@ewrealty.com" style={{ color: "#1e56a0", fontWeight: 600 }}>legal@ewrealty.com</a>.
                </p>
              </div>
            </div>
          </div>

          {/* 1. Introduction */}
          <section id="intro" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">01</span> Introduction &amp; Acceptance
            </h2>
            <p>
              This website, including any subdomains thereof, our mobile, tablet, and smart device applications, and application programming interfaces (collectively referred to as &ldquo;My Golden Homes&rdquo; or &ldquo;Platform&rdquo;) is owned, hosted, and operated by Info Edge (India) Ltd (hereinafter referred to as &ldquo;IEIL&rdquo;), a company incorporated under the Companies Act, 1956, having its registered office at Ground Floor, GF-12A, 94, Meghdoot, Nehru Place, New Delhi - 110 020.
            </p>
            <p>
              These terms and conditions, along with our Privacy Policy and Community Guidelines, constitute a legally binding agreement between My Golden Homes and the User (the &ldquo;Agreement&rdquo;). My Golden Homes provides an online information, communication, and real estate marketing platform subject to your strict compliance with the terms set forth herein.
            </p>
            <p>
              IEIL reserves the right to amend, alter, or modify these terms and conditions at any time. Any changes become effective immediately upon being published on the platform. Your continued use of My Golden Homes signifies your unconditional acceptance of all updated terms.
            </p>
          </section>

          {/* 2. Definitions */}
          <section id="definitions" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">02</span> Key Definitions
            </h2>
            <div className="mgh-card-grid-2">
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>Subscriber / Customer</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  Any individual or legal entity subscribed to the Services (free or paid) with dedicated sign-in credentials allotted or authorized by My Golden Homes.
                </p>
              </div>
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>Browser / Visitor</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  Any person utilizing or viewing non-restricted public areas of My Golden Homes without registering an account.
                </p>
              </div>
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>Services</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  The interactive online real estate information search tools, directory, banner advertisements, lead generation, and hosting medium provided on the platform.
                </p>
              </div>
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>RERDA</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  The Real Estate (Regulation and Development) Act, 2016, and all state rules, notifications, and regulations framed thereunder.
                </p>
              </div>
            </div>
            <p style={{ marginTop: "12px" }}>
              <strong>Notice:</strong> My Golden Homes acts purely as an advertising and information exchange medium. Under no circumstance does My Golden Homes act as an express or implied real estate agent, broker, or guarantor on behalf of any party.
            </p>
          </section>

          {/* 3. Listings & Advertisements */}
          <section id="listings" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">03</span> Submission &amp; Administration of Listings
            </h2>
            <p>
              Users agree not to submit property descriptions, photographs, pricing, or contact information unless they have acquired all necessary authorizations and power-of-attorney from the legal owner.
            </p>
            <ul className="mgh-custom-list">
              <li>Users warrant that all properties and associated specifications posted are truthful, current, and accurate.</li>
              <li>Postings may not be submitted under fictitious identities or names other than the authorized agent/owner.</li>
              <li>Users must disclose full factual details under RERDA, including project registration numbers, legal sanctions, and encumbrance status.</li>
              <li>My Golden Homes reserves the unilateral right to edit, reclassify, or remove any listing violating guidelines without prior notice.</li>
              <li>The &ldquo;Verified&rdquo; badge merely confirms basic existence verification of the listing; it does NOT constitute title verification, pricing appraisal, or legal clearance.</li>
            </ul>
          </section>

          {/* 4. Payment Terms & Refunds */}
          <section id="payment" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">04</span> Payment Terms &amp; Refund Policy
            </h2>
            <div className="mgh-card">
              <ul className="mgh-custom-list">
                <li>
                  <strong>100% Advance Basis:</strong> Payments for all subscriptions and advertising services on My Golden Homes are due strictly on a 100% advance basis.
                </li>
                <li>
                  <strong>Non-Refundable:</strong> Subscription amounts paid are non-refundable once activated. Any discretionary refunds are subject to internal audit and reconciliation.
                </li>
                <li>
                  <strong>Secure Gateway:</strong> Payments are processed via licensed third-party Payment Gateways. My Golden Homes does not store complete credit/debit card credentials on our servers.
                </li>
                <li>
                  <strong>Failed Transactions:</strong> While payment reconciliation works 24/7, refunds in failed transactions depend on banking inter-network timelines (typically 5&ndash;7 banking working days).
                </li>
              </ul>
            </div>
          </section>

          {/* 5. Rental Services */}
          <section id="rentals" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">05</span> Rental Property Services &amp; Agreements
            </h2>
            <p>
              Rental Property Services shall not be used for any unauthorized purpose other than bona-fide payment/collection of house rent or facilitation of rental agreements. Users are solely responsible for ensuring accuracy of bank account credentials and lease details.
            </p>
            <p>
              <strong>Chargebacks &amp; Disputes:</strong> In the event a transaction is disputed or reversed by the cardholder or issuing bank, the User agrees to promptly indemnify My Golden Homes for the full amount plus any banking fines.
            </p>
            <p>
              <strong>Tenant Verification:</strong> Verification services are performed through certified third-party verification partners. Express consent must be secured from the lessee before submitting personal identity documents.
            </p>
          </section>

          {/* 6. Video Guidelines */}
          <section id="video" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">06</span> Video Community Guidelines
            </h2>
            <p>
              Videos uploaded to accompany property listings must adhere to platform standards:
            </p>
            <ul className="mgh-custom-list">
              <li>Videos must be original walkthroughs free from copyright infringement.</li>
              <li>Content must not depict vulgarity, obscenity, or personal sensitive identity credentials.</li>
              <li>Videos undergo both automated AI and manual compliance screening prior to publishing.</li>
              <li>Videos hosted via YouTube API must comply with standard YouTube Terms of Service.</li>
            </ul>
          </section>

          {/* 7. Use of Information */}
          <section id="info-use" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">07</span> Use of Information &amp; Proprietary Rights
            </h2>
            <p>
              Users agree to treat all directory data, listings, and inquiries accessed through the platform as proprietary to My Golden Homes. Access is granted solely for internal, bona-fide real estate discovery. Reproduction, scraping, commercial distribution, or creation of derivative databases is strictly prohibited.
            </p>
          </section>

          {/* 8. Intellectual Property */}
          <section id="ip-rights" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">08</span> Intellectual Property Rights
            </h2>
            <p>
              All trademarks, service marks, logos, brand names, and source code appearing on My Golden Homes are the exclusive property of IEIL and its licensors. No right, license, or title is granted to any user. Any unauthorized replication constitutes an offence under the Trade Marks Act, 1999, and the Copyright Act, 1957.
            </p>
          </section>

          {/* 9. Restrictions */}
          <section id="restrictions" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">09</span> Explicit Restrictions &amp; Prohibitions
            </h2>
            <div className="mgh-card" style={{ background: "#fffbf0", borderLeft: "4px solid #f59e0b" }}>
              <p style={{ fontWeight: 600, color: "#92400e", margin: "0 0 10px 0" }}>
                The following actions are strictly prohibited and will lead to immediate account termination and legal action:
              </p>
              <ul className="mgh-custom-list" style={{ margin: 0 }}>
                <li>Using crawlers, scrapers, automated scripts, bots, or browser extensions to harvest platform data.</li>
                <li>Interfering with infrastructure security, server stability, rate limits, or Captcha challenges.</li>
                <li>Impersonating another person, broker, developer, or government authority.</li>
                <li>Posting content that is defamatory, obscene, promotes enmity, money laundering, or violates privacy.</li>
                <li>Reselling, sublicensing, or mirroring platform data for commercial competition.</li>
              </ul>
            </div>
          </section>

          {/* 10. Disclaimers */}
          <section id="disclaimers" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">10</span> Disclaimer of Warranties
            </h2>
            <p>
              <strong>Intermediary Status:</strong> My Golden Homes operates strictly as an intermediary under Section 2(w) of the Information Technology Act, 2000. All platform materials and listings are provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis without warranties of any kind.
            </p>
            <p>
              Users are strongly advised to independently verify physical premises, clear titles, municipal approvals, and RERA certifications before executing any sale or financial commitment. My Golden Homes does not guarantee responses or inquiries from listings.
            </p>
          </section>

          {/* 11. Liability */}
          <section id="liability" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">11</span> Limitation of Liability
            </h2>
            <p>
              Under no circumstance shall IEIL or its directors and staff be liable for direct, indirect, incidental, punitive, or consequential damages resulting from platform downtime, transaction disputes between buyers and sellers, technical errors, or unauthorized data access.
            </p>
          </section>

          {/* 12. Termination & Indemnification */}
          <section id="termination" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">12</span> Termination &amp; Indemnification
            </h2>
            <p>
              IEIL reserves the right to terminate access without prior notice if a user is suspected of fraudulent listings, terms breach, or harassment. Users agree to defend, indemnify, and hold harmless IEIL, its affiliates, directors, and employees against any losses, legal fees, or damages arising out of user content.
            </p>
          </section>

          {/* 13. Dealer & Builder Terms */}
          <section id="dealers" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">13</span> Specific Terms for Dealers &amp; Builders
            </h2>
            <p>
              Dealers, Builders, and Brokers accessing user leads must comply with all applicable Indian Data Protection Laws. Personal contact details shared may only be used for the specific property transaction inquiry. Sub-processing or unauthorized dissemination to non-affiliated telemarketers is strictly forbidden.
            </p>
          </section>

          {/* 14. Legal & Arbitration */}
          <section id="legal" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">14</span> Governing Law, Jurisdiction &amp; Arbitration
            </h2>
            <div className="mgh-card-grid-2">
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>Arbitration</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  Disputes shall be referred to a sole independent arbitrator under the Arbitration &amp; Conciliation Act, 1996. Venue of arbitration shall be New Delhi.
                </p>
              </div>
              <div className="mgh-card">
                <h4 style={{ color: "#1e56a0", margin: "0 0 6px 0" }}>Exclusive Jurisdiction</h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  The competent courts at New Delhi, India alone shall have exclusive jurisdiction in all matters relating to My Golden Homes.
                </p>
              </div>
            </div>
          </section>

          {/* 15. Grievances */}
          <section id="grievances" className="mgh-section">
            <h2 className="mgh-section-title">
              <span className="mgh-section-num">15</span> Grievances &amp; Legal Notices
            </h2>
            <p>
              For grievances regarding listings, content moderation, or terms violations, please contact our Legal Officer:
            </p>
            <div className="mgh-card">
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Legal &amp; Compliance Team:</strong> Info Edge (India) Limited
              </p>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Email:</strong>{" "}
                <a href="mailto:legal@ewrealty.com" style={{ color: "#1e56a0", fontWeight: 600 }}>legal@ewrealty.com</a>
              </p>
              <p style={{ margin: 0 }}>
                <strong>Grievance Portal:</strong>{" "}
                <Link to="/Grievance" style={{ color: "#1e56a0", fontWeight: 600 }}>Visit Grievance Cell</Link>
                {" | "}
                <Link to="/summonsnotices" style={{ color: "#1e56a0", fontWeight: 600 }}>Summons &amp; Notices</Link>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>

      {/* Bottom Reusable Contact Bar */}
      <ContactStrip />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsConditions;